// Scroll motion (07 Interactive Prototype polish.js; motion audit fix 8): blocks marked data-ns-reveal fade up 16px, progress
// bars fill from 0 to their width, and numbers marked data-ns-count count up to the number on the page, each once, as it
// scrolls into view. A progressive enhancement: the page renders final, so without this script, under reduced motion or
// without IntersectionObserver nothing changes. Only what is still below the window is touched, so nothing the shopper has
// seen jumps and the first screen never shifts. An element is armed (hidden, empty, zero) while it is still a quarter screen
// below the window and played once it is in view. Mark only start-aligned numbers: a centred one would move as it grows.
export const EASE = 'cubic-bezier(.2, .8, .2, 1)';
export const BARS = '.ns-bar__fill, .ns-progress__fill, .ns-cart-progress__bar span, .ns-buy__bar span';
const COUNT_MS = 900;
const BAR_MS = 1000;
// Native feel (2026-10-23): cards, list rows and section heads rise 10px as they scroll in, a row staggered 60ms apart (at
// most 5 steps), so a grid settles left to right instead of standing still. Cards inside a block that already reveals,
// and the setup looks (their own motion), are left alone. A card off to the side in a rail is never armed:
// it is already there when the rail scrolls to it.
export const RISE = '.ns-head, .ns-card, .ns-cs-card, .ns-post, .ns-list-card';
export const RISE_SKIP = '[data-ns-reveal], .ns-setup-looks, dialog';
export const RISE_MS = 520;
export const staggerDelay = index => Math.min(Math.max(0, index), 5) * 60;
// Photos below the window that are still loading wait transparent over their grey well and fade in once they arrive.
export const LATE_IMAGES = 'main img[loading="lazy"]';

// "$12,480", "94%", "4.8", "1,200+", "268 / 300". Not a year on its own, not "07", nothing under 3, no words.
const NUMBER = /^(\$)?(\d{1,3}(?:,\d{3})+|\d+)(\.\d)?(%|\+|\s?\/\s?[\d,]+)?$/;
export function parseCount(text) {
  const raw = String(text ?? '').trim();
  const match = NUMBER.exec(raw);
  if (!match || /^0\d/.test(raw)) return null;
  const [, prefix = '', digits, decimal = '', suffix = ''] = match;
  const value = Number(digits.replace(/,/g, '') + decimal);
  if (!(value >= 3)) return null;
  if (!prefix && !decimal && !suffix && /^\d{4}$/.test(digits) && value >= 1900 && value <= 2100) return null;
  return { prefix, value, decimals: decimal ? 1 : 0, suffix, grouped: digits.includes(','), text: raw };
}

export function formatCount(count, value) {
  const digits = count.grouped
    ? value.toLocaleString('en-US', { minimumFractionDigits: count.decimals, maximumFractionDigits: count.decimals })
    : value.toFixed(count.decimals);
  return `${count.prefix}${digits}${count.suffix}`;
}

export const easeOutCubic = p => 1 - (1 - p) ** 3;

// The text at `elapsed` ms; the last frame is the page's own text, so the count always ends on the real number.
export const countFrame = (count, elapsed, duration = COUNT_MS) => (elapsed >= duration ? count.text : formatCount(count, count.value * easeOutCubic(Math.max(0, elapsed) / duration)));

export function barTarget(el) {
  const width = el.style?.width ?? '';
  return /%$/.test(width) && parseFloat(width) > 0 ? width : null;
}

export function initMotion(doc, view) {
  if (view.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return null;
  if (typeof view.IntersectionObserver !== 'function') return null;
  const seen = new WeakSet();
  const state = new WeakMap();
  // Two observers, in this order: one arms an element a quarter screen before it shows, the other plays it in view.
  const arm = new view.IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      arm.unobserve(entry.target);
      state.get(entry.target)?.arm();
      play.observe(entry.target);
    }
  }, { rootMargin: '0px 0px 25% 0px', threshold: 0 });
  const play = new view.IntersectionObserver(entries => {
    let index = 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      play.unobserve(entry.target);
      state.get(entry.target)?.play(index++);
    }
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
  const watch = (el, effect) => {
    if (seen.has(el)) return;
    seen.add(el);
    if (!(el.getBoundingClientRect().top > view.innerHeight)) return;
    state.set(el, effect);
    arm.observe(el);
  };
  const reveal = el => ({
    arm: () => el.setAttribute('data-ns-reveal', 'wait'),
    play: () => el.setAttribute('data-ns-reveal', 'play'),
  });
  const bar = (el, target) => {
    let animation = null;
    return {
      arm: () => { animation = el.animate([{ width: '0%' }, { width: target }], { duration: BAR_MS, easing: EASE, fill: 'backwards' }); animation.pause(); },
      play: () => animation?.play(),
    };
  };
  const rise = el => {
    let animation = null;
    return {
      arm: () => {
        animation = el.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: RISE_MS, easing: EASE, fill: 'backwards' });
        animation.pause();
      },
      play: index => {
        const delay = staggerDelay(index);
        if (delay) view.setTimeout(() => animation?.play(), delay);
        else animation?.play();
      },
    };
  };
  const late = new WeakSet();
  const fadeIn = img => {
    if (late.has(img)) return;
    late.add(img);
    if (img.complete || !(img.getBoundingClientRect().top > view.innerHeight)) return;
    const done = () => {
      img.setAttribute('data-ns-img', 'in');
      view.setTimeout(() => img.removeAttribute('data-ns-img'), 500);
    };
    img.setAttribute('data-ns-img', 'wait');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  };
  const counter = (el, count) => ({
    arm: () => {
      el.style.minWidth = `${Math.ceil(el.getBoundingClientRect().width)}px`;
      if (view.getComputedStyle(el).display === 'inline') el.style.display = 'inline-block';
      el.textContent = countFrame(count, 0);
    },
    play: () => {
      let start = null;
      let last = el.textContent;
      const step = time => {
        if (!el.isConnected || el.textContent !== last) { el.style.minWidth = ''; return; }
        if (start === null) start = time;
        last = countFrame(count, time - start);
        el.textContent = last;
        if (last === count.text) el.style.minWidth = '';
        else view.requestAnimationFrame(step);
      };
      view.requestAnimationFrame(step);
    },
  });
  const scan = () => {
    for (const el of doc.querySelectorAll('[data-ns-reveal]')) watch(el, reveal(el));
    for (const el of doc.querySelectorAll(RISE)) {
      if (typeof el.animate !== 'function' || el.parentElement?.closest(RISE_SKIP)) continue;
      const box = el.getBoundingClientRect();
      if (box.right > 0 && box.left < view.innerWidth) watch(el, rise(el));
    }
    for (const img of doc.querySelectorAll(LATE_IMAGES)) fadeIn(img);
    for (const el of doc.querySelectorAll(BARS)) {
      const target = barTarget(el);
      if (target && typeof el.animate === 'function') watch(el, bar(el, target));
    }
    for (const el of doc.querySelectorAll('[data-ns-count]')) {
      const count = parseCount(el.textContent);
      const align = view.getComputedStyle(el).textAlign;
      if (count && ['start', 'left', 'justify'].includes(align)) watch(el, counter(el, count));
    }
  };
  scan();
  return scan;
}

if (typeof document !== 'undefined') {
  const start = () => {
    const scan = initMotion(document, window);
    if (!scan) return;
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('ns:cards', scan);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
